
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { isValidToken } from "@/utils/qrCodeGenerator";
import type { Database } from "@/integrations/supabase/types";

type Survey = Database["public"]["Tables"]["surveys"]["Row"];
type SupportedLanguage = Database["public"]["Enums"]["supported_language"];

// Initial survey to show on first scan (Consent + Background)
const FIRST_SURVEY_ID = "c3d4e5f6-a7b8-9012-3456-cdefab123456";

// Survey for BCAP (shown on all visits)
const BCAP_SURVEY_ID = "b2c3d4e5-f6a7-8901-2345-bcdefa123456";

// Survey for Leisure Activities (shown as first survey in 2nd and 3rd visits)
const LEISURE_ACTIVITIES_ID = "d4e5f6a7-b8c9-0123-4567-defabc123456";

// Survey for Social Services (shown as final survey in each visit)
const SOCIAL_SERVICES_ID = "a1b2c3d4-e5f6-7890-1234-abcdef123456";

interface TokenValidatorProps {
  token: string;
  currentLanguage?: SupportedLanguage;
  onValidToken: (survey: Survey, completedSteps: number, nextResponseNumber: number, language?: SupportedLanguage) => void;
  onInvalidToken: (error: string) => void;
}

export const TokenValidator = ({
  token,
  currentLanguage,
  onValidToken,
  onInvalidToken,
}: TokenValidatorProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const validateAndLoadSurvey = async () => {
    try {
      console.log('Starting survey validation for token:', token);
      console.log('Current language from TokenValidator:', currentLanguage);
      
      // Validate token format first
      if (!isValidToken(token)) {
        console.error('Invalid token format:', token);
        onInvalidToken("Invalid token format. Please check the token and try again.");
        return;
      }
      
      // Use secure token validation function
      const { data: tokenValidation, error: validationError } = await supabase
        .rpc('validate_auth_token', { token_value: token });
      
      if (validationError) {
        console.error('Token validation error:', validationError);
        throw new Error("Failed to validate token");
      }
      
      // Parse the JSON response from the validation function
      const validationResult = typeof tokenValidation === 'string' 
        ? JSON.parse(tokenValidation) 
        : tokenValidation;
      
      if (!validationResult?.valid) {
        console.error('Invalid token:', token);
        onInvalidToken("This survey token does not exist or is not linked to a survey.");
        return;
      }

      console.log('Token validation successful:', tokenValidation);
      
      // Update token usage tracking using secure function
      const { error: updateError } = await supabase
        .rpc('update_token_usage', { token_value: token });
      
      if (updateError) {
        console.error('Error updating token usage:', updateError);
        // Continue even if update fails
      }

      // Get token progression from the secure public table
      const { data: progression, error: progressionError } = await supabase
        .from("token_progression")
        .select("response_count, last_response_at")
        .eq("token", token)
        .maybeSingle();

      if (progressionError && progressionError.code !== 'PGRST116') {
        console.error('Error fetching token progression:', progressionError);
        throw new Error("Failed to fetch token progression");
      }

      const responseCount = progression?.response_count || 0;
      console.log(`Found ${responseCount} previous responses for token ${token}`);
      
      const nextResponseNumber = responseCount + 1;
      console.log(`Next response_number will be: ${nextResponseNumber}`);
      
      // Get the most recently used language (if any) - we need to query survey_responses for this
      // but only when authenticated (for group leaders) or for the specific token
      let mostRecentLanguage = currentLanguage;
      
      if (!mostRecentLanguage && responseCount > 0) {
        // Try to get the most recent language from survey responses
        const { data: recentResponse } = await supabase
          .from("survey_responses")
          .select("language")
          .eq("auth_token", token)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        
        if (recentResponse?.language) {
          mostRecentLanguage = recentResponse.language as SupportedLanguage;
          console.log(`Found most recent language from responses: ${mostRecentLanguage}`);
        }
      }
      
      // Check if user has completed a full visit (every 3 surveys)
      const isCompletedVisit = responseCount % 3 === 0 && responseCount > 0;
      
      // Calculate days since last response
      const lastResponseDate = progression?.last_response_at 
        ? new Date(progression.last_response_at) 
        : null;
      
      const daysSinceLastResponse = lastResponseDate 
        ? (Date.now() - lastResponseDate.getTime()) / (1000 * 60 * 60 * 24)
        : 0;
      
      const hasWaitedLongEnough = daysSinceLastResponse >= 3; // 3 days
      const isFinalCompletion = responseCount >= 9;
      
      console.log(`Response count: ${responseCount}, is completed visit: ${isCompletedVisit}`);
      console.log(`Days since last response: ${daysSinceLastResponse.toFixed(1)}`);
      console.log(`Has waited long enough (>=21 days): ${hasWaitedLongEnough}`);
      console.log(`Is final completion (>=9 surveys): ${isFinalCompletion}`);
      
      // Only redirect to completion if visit is complete AND (hasn't waited OR is final)
      if (isCompletedVisit && (!hasWaitedLongEnough || isFinalCompletion)) {
        console.log(`Redirecting to completion page. Reason: ${
          isFinalCompletion 
            ? 'All surveys completed' 
            : `Must wait ${(21 - daysSinceLastResponse).toFixed(1)} more days`
        }`);
        
        const lastSubmissionDate = lastResponseDate || new Date();
        
        // Navigate to complete page after every 3rd survey
        navigate("/survey-complete", { 
          state: { 
            participantGroup: validationResult.assigned_group,
            token,
            language: mostRecentLanguage,
            waitingForNextVisit: !isFinalCompletion,
            lastSubmissionDate: lastSubmissionDate.toISOString(),
            completedVisits: Math.floor(responseCount / 3),
            totalResponses: responseCount
          } 
        });
        return;
      }
      
      // If we reach here and visit is complete but user has waited long enough,
      // continue to load the next survey
      if (isCompletedVisit && hasWaitedLongEnough && !isFinalCompletion) {
        console.log(`Visit complete but user has waited ${daysSinceLastResponse.toFixed(1)} days. Allowing next visit.`);
      }
      
      // Determine which survey to show based on responseCount (number of completed surveys)
      // responseCount represents how many surveys have been completed
      // So responseCount becomes the index of the next survey to show (0-indexed)
      let surveyId: string;
      const effectiveStep = responseCount;
      
      console.log(`Loading survey for step ${effectiveStep} (${responseCount} surveys completed)`);
      
      // Survey progression logic based on effective step
      if (effectiveStep === 0) {
        // First visit, first survey: Consent + Background
        surveyId = FIRST_SURVEY_ID;
        console.log("Loading Background survey (step 0)");
      } else if (effectiveStep === 1) {
        // First visit, second survey: BCAP
        surveyId = BCAP_SURVEY_ID;
        console.log("Loading BCAP survey (step 1)");
      } else if (effectiveStep === 2) {
        // First visit, third survey: Social Services
        surveyId = SOCIAL_SERVICES_ID;
        console.log("Loading Social Services survey (step 2)");
      }
      // Second Visit Logic
      else if (effectiveStep === 3) {
        // Second visit, first survey: Leisure Activities
        surveyId = LEISURE_ACTIVITIES_ID;
        console.log("Loading Leisure Activities survey (step 3)");
      } else if (effectiveStep === 4) {
        // Second visit, second survey: BCAP
        surveyId = BCAP_SURVEY_ID;
        console.log("Loading BCAP survey (step 4)");
      } else if (effectiveStep === 5) {
        // Second visit, third survey: Social Services
        surveyId = SOCIAL_SERVICES_ID;
        console.log("Loading Social Services survey (step 5)");
      }
      // Third Visit Logic
      else if (effectiveStep === 6) {
        // Third visit, first survey: Leisure Activities
        surveyId = LEISURE_ACTIVITIES_ID;
        console.log("Loading Leisure Activities survey (step 6)");
      } else if (effectiveStep === 7) {
        // Third visit, second survey: BCAP
        surveyId = BCAP_SURVEY_ID;
        console.log("Loading BCAP survey (step 7)");
      } else if (effectiveStep === 8) {
        // Third visit, third survey: Social Services
        surveyId = SOCIAL_SERVICES_ID;
        console.log("Loading Social Services survey (step 8)");
      } else {
        // Show the most recent survey with previously submitted answers
        console.log('All survey parts completed, showing most recent survey with answers:', token);
        surveyId = SOCIAL_SERVICES_ID; // Show the last survey type
      }

      console.log(`Loading survey ${surveyId} (effective step: ${effectiveStep}, next response number: ${nextResponseNumber}, language: ${mostRecentLanguage || 'default'})`);

      // Get the survey
      const { data: surveyData, error: surveyError } = await supabase
        .from("surveys")
        .select("*")
        .eq("id", surveyId)
        .maybeSingle();

      if (surveyError) {
        console.error('Survey fetch error:', surveyError);
        throw new Error("Failed to fetch survey");
      }
      if (!surveyData) {
        console.error('No survey found for ID:', surveyId);
        onInvalidToken("The survey could not be found.");
        return;
      }

      console.log('Successfully loaded survey:', surveyData);
      onValidToken(surveyData, effectiveStep, nextResponseNumber, mostRecentLanguage);
    } catch (error) {
      console.error("Error in survey validation:", error);
      onInvalidToken("There was a problem loading the survey. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Validate and load survey directly without delay
    // The useSurvey hook now handles progression internally
    validateAndLoadSurvey();
  }, [token]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <p>Loading survey...</p>
        </div>
      </div>
    );
  }

  return null;
};
